/**
 * The notification store — the queue behind the Flow-F toast layer and
 * sticky banner (ADR-029 §5, REQ-UI-062).
 *
 * A zustand store, following the ``alerts/alerts-store`` pattern (plain
 * ``create``, actions on the state object, ``now`` injectable for
 * deterministic tests) so the two read alike.
 *
 * # Two queues, deliberately
 *
 * ``toasts`` is **capped** at :data:`notification-timing.TOAST_STACK_CAP`
 * and evicts oldest-first — the bottom-right overlay has finite room, and
 * ``chrome-spec.md`` specifies the eviction. ``banners`` is **uncapped**:
 * CRITICAL conditions must not be discarded, so extras are retained and
 * the host renders ``banners[0]`` with a "+N more" expander for the rest
 * (Flow F §Behavior notes; ADR-029 §Consequences — "operators see at most
 * one sticky banner at a time, prioritized by recency").
 *
 * Eviction from ``toasts`` is not data loss for a SAPIENT alert: the
 * Alerts panel is the durable record and the alert stays there until
 * dismissed. The overlay is a *notification* of the alert, not the alert.
 *
 * # ``close()`` is local. Dismissal is not.
 *
 * See :file:`notification-types.ts` §"Local close vs system-wide
 * dismissal". ``close()`` and :func:`NotificationsState.closeByDedupeKey`
 * drop the entry and invoke **nothing** — no action callback of any kind,
 * ``systemWide`` or otherwise. A SAPIENT alert's dismissal reaches every
 * operator on the network (ADR-029 §7), so it only ever happens through an
 * explicit labelled action the operator chose. Both behaviours are pinned
 * by tests; do not "simplify" them into one path.
 *
 * Cross-references:
 *   - ADR-029 §5 / §7 / §10 / [MVP] item 6 (cross-session fan-out).
 *   - REQ-UI-062; REQ-UI-054; REQ-UI-056 (display-only).
 *   - storyboard Flow F; ``storyboards/shared/chrome-spec.md`` §"Toast layer".
 *   - feature-specs/alert-notification-surface/feature-spec.md §0.2.
 */
import { useMemo } from "react";
import { create } from "zustand";
import {
  TOAST_STACK_CAP,
  notificationDurationMs,
  notificationSlot,
} from "@/notifications/notification-timing";
import type {
  Notification,
  NotificationId,
  NotificationInput,
} from "@/notifications/notification-types";

/**
 * Monotonic id counter. A counter rather than a timestamp or random value
 * so ids are stable and collision-free under the same-millisecond bursts
 * an alert flood produces (ADR-029 §10), and so tests are deterministic
 * without stubbing a clock.
 */
let _seq = 0;

function _nextId(): NotificationId {
  _seq += 1;
  return `ntf-${_seq}` as NotificationId;
}

/** Optional knobs for {@link NotificationsState.notify}. */
export interface NotifyOptions {
  /**
   * The reference "now" (epoch ms) stamped as ``createdAt``. Defaults to
   * ``Date.now()``; tests pass an explicit value for determinism.
   */
  readonly now?: number;
  /**
   * Whether the UI is in display-only mode (REQ-UI-056). Forwarded to
   * :func:`notification-timing.notificationDurationMs`, which gives an
   * otherwise-sticky toast a bounded expiry so an unattended wall display
   * self-heals (feature-spec §0.1 D4).
   */
  readonly displayOnly?: boolean;
}

/** The notification store shape. */
export interface NotificationsState {
  /** Live toasts, newest first. Capped at {@link TOAST_STACK_CAP}. */
  readonly toasts: readonly Notification[];
  /**
   * Live CRITICAL banners, newest first. Uncapped — ``[0]`` is the active
   * banner; the remainder are the "+N more" backlog.
   */
  readonly banners: readonly Notification[];
  /**
   * Raise a notification. Returns its id.
   *
   * Routes by severity (``critical`` → banner, else toast), resolves the
   * duration once at raise time, and collapses onto an existing live entry
   * when ``dedupeKey`` matches — replacing it **in place** so the operator
   * does not have to re-find a notification that merely changed.
   */
  readonly notify: (input: NotificationInput, opts?: NotifyOptions) => NotificationId;
  /**
   * Remove a notification from this operator's view. **Local only** —
   * invokes no action callback. Idempotent for an unknown id.
   */
  readonly close: (id: NotificationId) => void;
  /**
   * Remove whichever notification carries ``dedupeKey``. The seam the
   * cross-session dismissal fan-out uses: when any operator dismisses an
   * alert, every session closes its live notification for that alert
   * without needing to know the local notification id (ADR-029 [MVP] item
   * 6). Local only, like {@link close}.
   */
  readonly closeByDedupeKey: (dedupeKey: string) => void;
  /**
   * Suspend or resume a notification's auto-dismiss countdown. Driven by
   * hover / focus-within so reading a toast cannot make it vanish
   * mid-sentence (Flow F §Accessibility). No-op for an unknown id.
   */
  readonly setPaused: (id: NotificationId, paused: boolean) => void;
  /** Drop every live notification. Used on sign-out and by tests. */
  readonly clear: () => void;
}

/**
 * Replace the entry sharing ``dedupeKey`` in place, preserving its
 * position, or return ``null`` when no live entry matches.
 */
function _replaceInPlace(
  queue: readonly Notification[],
  next: Notification,
): readonly Notification[] | null {
  if (next.dedupeKey === undefined) {
    return null;
  }
  const at = queue.findIndex((n) => n.dedupeKey === next.dedupeKey);
  if (at === -1) {
    return null;
  }
  // Keep the original id so an outstanding close(id) still addresses it.
  const merged: Notification = { ...next, id: queue[at].id };
  return [...queue.slice(0, at), merged, ...queue.slice(at + 1)];
}

export const useNotificationStore = create<NotificationsState>((set) => ({
  toasts: [],
  banners: [],

  notify: (input, opts = {}) => {
    const slot = notificationSlot(input.severity);
    const id = _nextId();
    const candidate: Notification = {
      ...input,
      id,
      slot,
      createdAt: opts.now ?? Date.now(),
      // ``sticky`` removes the timer entirely; otherwise the severity's
      // ratified duration applies (feature-spec §0.1 D2 / D4).
      durationMs:
        input.sticky === true
          ? null
          : notificationDurationMs(input.severity, { displayOnly: opts.displayOnly }),
      paused: false,
    };

    let assigned = id;
    set((state) => {
      const queue = slot === "banner" ? state.banners : state.toasts;
      const replaced = _replaceInPlace(queue, candidate);
      if (replaced !== null) {
        // Dedupe hit: the in-place entry keeps its original id, so report
        // that id back to the caller rather than the unused new one.
        assigned = replaced.find((n) => n.dedupeKey === candidate.dedupeKey)?.id ?? id;
        return slot === "banner" ? { banners: replaced } : { toasts: replaced };
      }
      const grown = [candidate, ...queue];
      if (slot === "banner") {
        // Uncapped — a CRITICAL condition is never discarded.
        return { banners: grown };
      }
      // Cap the toast stack, dropping the oldest (chrome-spec.md).
      return { toasts: grown.slice(0, TOAST_STACK_CAP) };
    });
    return assigned;
  },

  close: (id) =>
    set((state) => ({
      toasts: state.toasts.filter((n) => n.id !== id),
      banners: state.banners.filter((n) => n.id !== id),
    })),

  closeByDedupeKey: (dedupeKey) =>
    set((state) => ({
      toasts: state.toasts.filter((n) => n.dedupeKey !== dedupeKey),
      banners: state.banners.filter((n) => n.dedupeKey !== dedupeKey),
    })),

  setPaused: (id, paused) =>
    set((state) => ({
      toasts: state.toasts.map((n) => (n.id === id ? { ...n, paused } : n)),
      banners: state.banners.map((n) => (n.id === id ? { ...n, paused } : n)),
    })),

  clear: () => set({ toasts: [], banners: [] }),
}));

/**
 * The active CRITICAL banner and the size of its backlog.
 *
 * Flow F shows one banner at a time with a "+N more" pill for additional
 * conditions; this is the selector that split feeds. ``active`` is ``null``
 * when nothing critical is live.
 */
export function useActiveBanner(): { active: Notification | null; overflowCount: number } {
  const banners = useNotificationStore((s) => s.banners);
  return useMemo(
    () => ({
      active: banners.length > 0 ? banners[0] : null,
      overflowCount: Math.max(0, banners.length - 1),
    }),
    [banners],
  );
}
